import React, { useEffect, useRef } from "react";
import { Input, Text } from "components-lib";
/**
 * ClinicalFindingsSection Component
 * Manages clinical findings with finding number and finding text
 * Similar to advice section but without autocomplete functionality
 */
const ClinicalFindingsSection = ({
  templateData,
  clinicalFindingsData,
  setClinicalFindingsData,
  newClinicalFinding,
  setNewClinicalFinding,
  appointmentId,
  appointmentPrescription,
}) => {
  // Add clinical finding handler
  const handleAddClinicalFinding = () => {
    const findingText = newClinicalFinding?.finding?.toString?.() || "";
    if (findingText.trim()) {
      const findingToAdd = {
        finding_no: `F${clinicalFindingsData.length + 1}`,
        finding: findingText.trim(),
      };

      setClinicalFindingsData([...clinicalFindingsData, findingToAdd]);
      setNewClinicalFinding({
        finding: "",
      });
    }
  };

  // Delete clinical finding handler
  const handleDeleteClinicalFinding = (index) => {
    setClinicalFindingsData(clinicalFindingsData.filter((_, i) => i !== index));
  };

  const handleAddApiClinicalFinding = (apiFinding) => {
    const existsInFindings = clinicalFindingsData.some(
      (finding) =>
        finding.finding.toLowerCase() === apiFinding.finding.toLowerCase()
    );

    if (!existsInFindings) {
      const findingToAdd = {
        finding_no: apiFinding.finding_no, // Use the finding_no from the API
        finding: apiFinding.finding.trim(),
      };

      setClinicalFindingsData((prevFindings) => [...prevFindings, findingToAdd]);
    }
  };

  const hydratedFromPrescriptionRef = useRef(null);

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const apiClinicalFindings = Array.isArray(source?.clinical_findings)
        ? source.clinical_findings
        : Array.isArray(source?.prescription?.clinical_findings)
          ? source.prescription.clinical_findings
          : [];

      apiClinicalFindings.forEach((apiFinding) => {
        if (apiFinding?.finding) {
          handleAddApiClinicalFinding(apiFinding);
        }
      });

      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(
        `Error hydrating clinical findings for appointment ${appointmentId}:`,
        error,
      );
    }
  }, [appointmentId, appointmentPrescription]);

  useEffect(() => {
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      const templateClinicalFindings = Array.isArray(parsed.clinical_findings)
        ? parsed.clinical_findings
        : Array.isArray(parsed)
        ? parsed
        : [];

      const normalized = templateClinicalFindings
        .map((cf) => ({
          finding_no: cf.finding_no ?? `F${clinicalFindingsData.length + 1}`,
          finding: (cf.finding ?? "").toString().trim(),
        }))
        .filter((cf) => cf && cf.finding);

      if (normalized.length === 0) return;

      setClinicalFindingsData((prev = []) => {
        const existing = Array.isArray(prev) ? prev : [];

        const toAdd = [];

        const isDuplicateAgainst = (candidate, target) => {
          // match by finding text case-insensitive
          if (
            target.finding &&
            candidate.finding &&
            target.finding.toLowerCase() === candidate.finding.toLowerCase()
          ) {
            return true;
          }
          return false;
        };

        normalized.forEach((tcf) => {
          const existsInExisting = existing.some((ecf) => isDuplicateAgainst(tcf, ecf));
          const existsInToAdd = toAdd.some(
            (added) => added.finding.toLowerCase() === tcf.finding.toLowerCase()
          );

          if (!existsInExisting && !existsInToAdd) {
            toAdd.push({
              ...tcf,
              finding_no: tcf.finding_no || `F${existing.length + toAdd.length + 1}`,
            });
          }
        });

        if (toAdd.length === 0) return existing;

        // clear newClinicalFinding similar to API flow
        setNewClinicalFinding({
          finding: "",
        });

        return [...existing, ...toAdd];
      });
    } catch (err) {
      console.warn("Failed to parse/apply templateData clinical_findings:", err);
    }
  }, [templateData, setClinicalFindingsData, setNewClinicalFinding]);

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Clinical Findings:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        {/* Clinical Finding Input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <Input
            value={newClinicalFinding.finding}
            onChange={(e) =>
              setNewClinicalFinding({
                ...newClinicalFinding,
                finding: e.target.value,
              })
            }
            placeholder="Enter clinical finding..."
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #E2E8F0",
            }}
          />

          <button
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              padding: "8px 16px",
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddClinicalFinding}
            disabled={!newClinicalFinding?.finding?.toString?.()?.trim()}
          >
            Add
          </button>
        </div>

        {/* Selected Clinical Findings Container */}
        {clinicalFindingsData.length > 0 && (
          <div
            style={{
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              padding: "15px",
              backgroundColor: "#f9f9f9",
              marginTop: "15px",
            }}
          >
            <Text
              fontWeight="bold"
              style={{ fontSize: "14px", marginBottom: "10px" }}
            >
              Clinical Findings:
            </Text>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {clinicalFindingsData.map((finding, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "8px",
                    padding: "10px 15px",
                    fontSize: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <span>{finding.finding}</span>
                  </div>
                  <button
                    style={{
                      backgroundColor: "#e2e8f0",
                      color: "red",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "50%",
                      cursor: "pointer",
                      padding: "2px 6px",
                      fontSize: "12px",
                      lineHeight: "1",
                      minWidth: "20px",
                      height: "20px",
                    }}
                    onClick={() => handleDeleteClinicalFinding(index)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export default ClinicalFindingsSection;
